/**
 * The plot's frame in the world: the group the measured DTM plot hangs
 * under, and where that group is mounted.
 */
import * as THREE from 'three';

import type { WorldVec } from '../world/frame.ts';
import type { AtlasLive } from '../atlas';
import type { MountHandle } from './mount.ts';

export interface PlotMountDeps {
  readonly mount: MountHandle;
  own(fn: () => void): void;
  readonly live: AtlasLive;
}

export interface PlotMountHandle {
  readonly dtmGroup: THREE.Group;
  readonly plotWorld: WorldVec;
  readonly plotQuat: THREE.Quaternion;
  readonly plotUpWorld: THREE.Vector3;
}

export function createPlotMount(deps: PlotMountDeps): PlotMountHandle {
  const { own } = deps;
  const { atlasRoot, worldFrame } = deps.mount;

  // T5 — the measured plot. Loaded on demand: 2 MB of elevation and 2 MB of
  // orthoimage is not worth fetching for a player who never leaves the
  // heliocentric view.
  const dtmGroup = new THREE.Group();
  dtmGroup.name = 'dtm-plot';
  dtmGroup.visible = false;

  /**
   * The plot's own frame, and where it stands in the world.
   *
   * `plot.group` hangs under `dtmGroup` and keeps its own recentre, so the
   * walkaround rig below measures in the plot's own y-up metres about the
   * cluster centroid - the same numbers it read when that frame WAS the scene.
   * `dtmGroup` carries the rest: `plotWorld` is that centroid's heliocentric
   * position and `plotQuat` the site's east/up/south basis turned by the body's
   * own spin, both written each tick by `syncPlotFrame` and handed to the world
   * frame the way `detailGroup`'s are.
   */
  const plotWorld: WorldVec = { x: 0, y: 0, z: 0 };
  const plotQuat = new THREE.Quaternion();
  /** The plot frame's own up, in the drawn frame: the site's radial. */
  const plotUpWorld = new THREE.Vector3(0, 1, 0);
  let plotFrameRelease: (() => void) | null = null;
  function syncPlotMount(): void {
    if (plotFrameRelease !== null) return;
    atlasRoot.remove(dtmGroup);
    plotFrameRelease = worldFrame.register(dtmGroup, () => plotWorld);
    deps.live.needsRender = true;
  }
  syncPlotMount();
  own(() => { plotFrameRelease?.(); plotFrameRelease = null; });

  return { dtmGroup, plotWorld, plotQuat, plotUpWorld };
}
